import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * Full-screen asset viewer shared by the storyboard, dailies, design and footage
 * galleries — each previously hand-rolled its own. Escape closes; arrow keys step
 * through when `onPrev`/`onNext` are supplied.
 */
export default function Lightbox({
  label,
  onClose,
  onPrev,
  onNext,
  children,
  footer,
}: {
  label?: React.ReactNode;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  children: React.ReactNode;
  /** Extra content below the asset (e.g. the shot references strip). */
  footer?: React.ReactNode;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev?.();
      if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-[60] flex cursor-pointer items-center justify-center bg-black/85 p-8" onClick={onClose}>
      <button
        onClick={onClose}
        aria-label={t('action.close')}
        className="absolute right-5 top-5 rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>

      {onPrev && (
        <button
          aria-label={t('action.previous', { defaultValue: 'Previous' })}
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 rounded-full bg-black/60 p-2 text-white/80 hover:bg-black/80 hover:text-white"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      {onNext && (
        <button
          aria-label={t('action.next', { defaultValue: 'Next' })}
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 rounded-full bg-black/60 p-2 text-white/80 hover:bg-black/80 hover:text-white"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      <div className="relative max-h-full w-full max-w-6xl cursor-default overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
        {label && <div className="mt-2 inline-block rounded bg-black/70 px-3 py-1.5 font-mono text-xs text-white">{label}</div>}
        {footer}
      </div>
    </div>
  );
}
